import { useEffect, useRef } from 'react';

/**
 * Paints peer text highlights in the local DOM. Auto-clears the previous
 * wrap when a new highlight (or empty) arrives.
 *
 * Algorithm:
 *  1. Walk *all* visible text nodes (skipping <script>/<style>/etc and our
 *     own UI overlays), concatenating them into one string while tracking
 *     each node's offset window in that combined string.
 *  2. Find the first occurrence of the search text in the combined string.
 *  3. Walk the matched range across however many text nodes it spans —
 *     including ones nested inside inline elements like <strong>, which is
 *     the common case for the chapter passage where the focal word is
 *     wrapped in <strong>. Wrap each per-node segment in its own span.
 *
 * Limitations (acceptable for iteration B):
 *  - Matches only the *first* global occurrence of the search text.
 *  - Transient — proper anchor-stable annotations are iteration D.
 */
export default function RemoteHighlights({ highlights = {}, selfUserId }) {
  const wrappedRef = useRef(new Map()); // user_id -> array of wrapper spans

  useEffect(() => {
    Object.entries(highlights).forEach(([userId, h]) => {
      if (String(userId) === String(selfUserId)) return;

      // Tear down previous wrap for this user
      const prev = wrappedRef.current.get(userId);
      if (prev) {
        unwrap(prev);
        wrappedRef.current.delete(userId);
      }

      const text = (h?.text || '').trim();
      if (!text) return;

      const isTeacher = h.role === 'teacher';
      const color = isTeacher
        ? 'rgba(59, 130, 246, 0.55)' // blue-500/55
        : 'rgba(16, 185, 129, 0.55)'; // emerald-500/55
      const outline = isTeacher
        ? 'rgba(59, 130, 246, 0.95)'
        : 'rgba(16, 185, 129, 0.95)';

      const wrappers = wrapFirstMatch(text, color, outline);
      if (wrappers.length > 0) {
        try {
          wrappers[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {
          /* no-op */
        }
        wrappedRef.current.set(userId, wrappers);
      }
    });
  }, [highlights, selfUserId]);

  return null;
}

function isAcceptableTextNode(node) {
  const parent = node.parentElement;
  if (!parent) return false;
  const tag = parent.tagName;
  if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT' || tag === 'TEMPLATE') {
    return false;
  }
  if (
    parent.closest(
      '.remote-highlight, [data-no-highlight], button, input, textarea, select, [data-live-session-controller], header, nav'
    )
  ) {
    return false;
  }
  return true;
}

function buildHighlightSpan(color, outline) {
  const span = document.createElement('span');
  span.className = 'remote-highlight';
  // Inline + !important so parent fade-out classes can't wash it out.
  span.setAttribute(
    'style',
    [
      `background-color: ${color} !important`,
      `box-shadow: 0 0 0 2px ${outline || color} !important`,
      `color: inherit !important`,
      `opacity: 1 !important`,
      `border-radius: 3px`,
      `padding: 0 2px`,
      `transition: background-color 200ms ease, box-shadow 200ms ease`,
    ].join('; ')
  );
  return span;
}

function wrapFirstMatch(text, color, outline) {
  if (typeof document === 'undefined' || !text) return [];

  // Collect all visible text nodes plus their offsets in the concatenated
  // string. We deliberately don't filter on text content here so the search
  // can span multiple nodes (e.g. through a <strong>).
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      isAcceptableTextNode(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
  });

  const nodes = [];
  let combined = '';
  while (walker.nextNode()) {
    const n = walker.currentNode;
    const start = combined.length;
    combined += n.nodeValue;
    nodes.push({ node: n, start, end: combined.length });
  }

  const idx = combined.indexOf(text);
  if (idx < 0) return [];
  const endIdx = idx + text.length;

  // For each text node that overlaps [idx, endIdx), capture the offsets
  // *within that node* that need to be wrapped.
  const segments = [];
  for (const nr of nodes) {
    if (nr.end <= idx) continue;
    if (nr.start >= endIdx) break;
    const from = Math.max(0, idx - nr.start);
    const to = Math.min(nr.node.nodeValue.length, endIdx - nr.start);
    if (to > from) segments.push({ node: nr.node, from, to });
  }

  // Wrap each segment in its own span. Each wrap is entirely within a
  // single text node, so range.surroundContents always succeeds. The
  // resulting siblings sit naturally next to (or inside) any inline
  // formatting elements like <strong>.
  const wrappers = [];
  for (const seg of segments) {
    const range = document.createRange();
    try {
      range.setStart(seg.node, seg.from);
      range.setEnd(seg.node, seg.to);
    } catch {
      continue;
    }
    const span = buildHighlightSpan(color, outline);
    try {
      range.surroundContents(span);
      wrappers.push(span);
    } catch {
      /* skip */
    }
  }
  return wrappers;
}

function unwrap(spans) {
  const list = Array.isArray(spans) ? spans : [spans];
  for (const span of list) {
    const parent = span.parentNode;
    if (!parent) continue;
    while (span.firstChild) parent.insertBefore(span.firstChild, span);
    parent.removeChild(span);
    parent.normalize();
  }
}
